import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { typographyVariantStyles } from '../../typography-variants';
import type { AlertSize, AlertVariant } from './types';

const SIZE_PADDING: Record<AlertSize, string> = {
  md: '8px 12px',
  lg: '12px 16px',
};

function getBackground(theme: Theme, variant: AlertVariant): string {
  switch (variant) {
    case 'success':
      return theme.colors.brand.subtle;
    case 'warning':
      return theme.colors.warning.subtle;
    case 'error':
      return theme.colors.error.subtle;
    case 'info':
      return theme.colors.info.subtle;
    default:
      return theme.colors.background.raised;
  }
}

function getIconColor(theme: Theme, variant: AlertVariant): string {
  switch (variant) {
    case 'success':
      return theme.colors.brand.text;
    case 'warning':
      return theme.colors.warning.text;
    case 'error':
      return theme.colors.error.text;
    case 'info':
      return theme.colors.info.text;
    default:
      return theme.colors.text.primary;
  }
}

export const StyledAlert = styled('div', {
  shouldForwardProp: (prop) => prop !== '$variant' && prop !== '$size',
})<{ $variant: AlertVariant; $size: AlertSize }>(({ theme, $variant, $size }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing[12],
  width: '100%',
  padding: SIZE_PADDING[$size],
  borderRadius: theme.radius[12],
  backgroundColor: getBackground(theme, $variant),
}));

export const StyledIconWrapper = styled('span', {
  shouldForwardProp: (prop) => prop !== '$variant' && prop !== '$hasLargeTitle',
})<{ $variant: AlertVariant; $hasLargeTitle: boolean }>(({ theme, $variant, $hasLargeTitle }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  height: ($hasLargeTitle ? typographyVariantStyles.body2 : typographyVariantStyles.body3)
    .lineHeight,
  color: getIconColor(theme, $variant),

  '& svg': {
    width: 16,
    height: 16,
  },
}));

export const StyledContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 0 0',
  minWidth: 0,
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: theme.spacing[12],
}));

export const StyledText = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[2],
  width: '100%',
  wordBreak: 'break-word',
}));

export const StyledTitle = styled('p', {
  shouldForwardProp: (prop) => prop !== '$size',
})<{ $size: AlertSize }>(({ theme, $size }) => ({
  margin: 0,
  ...($size === 'lg' ? typographyVariantStyles.body2 : typographyVariantStyles.body3),
  fontWeight: 500,
  color: theme.colors.text.primary,
}));

export const StyledDescription = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[4],
  margin: 0,
  ...typographyVariantStyles.body3,
  color: theme.colors.text.secondary,

  '& p, & ul, & ol': {
    margin: 0,
  },

  '& ul, & ol': {
    paddingLeft: theme.spacing[20],
  },

  '& li + li': {
    marginTop: theme.spacing[4],
  },
}));
